import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import DOMPurify from 'dompurify';
import 'react-quill-new/dist/quill.snow.css';
import { Eye, EyeOff, Save, RotateCcw, RotateCw } from 'lucide-react';

const MAX_CHARS = 5000;
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

export const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder,
  maxChars = MAX_CHARS,
  onAutoSave,
  draftKey
}) => {
  const quillRef = useRef(null);
  const [isPreview, setIsPreview] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Calculate character count (strip HTML)
  useEffect(() => {
    const text = value?.replace(/<[^>]*>/g, '') || '';
    setCharCount(text.length);
  }, [value]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!draftKey || !isDirty) return;

    const interval = setInterval(() => {
      if (isDirty && value) {
        localStorage.setItem(`draft_${draftKey}`, value);
        setLastSaved(new Date());
        setIsDirty(false);
        if (onAutoSave) onAutoSave();
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(interval);
  }, [isDirty, value, draftKey, onAutoSave]);

  // Load draft on mount - only runs once when component mounts with no initial value
  useEffect(() => {
    // Skip if there's already a value (editing existing content)
    if (value) return;
    
    if (draftKey) {
      const saved = localStorage.getItem(`draft_${draftKey}`);
      if (saved) {
        onChange(saved);
        setLastSaved(new Date());
      }
    }
  }, [draftKey, value, onChange]);

  // Handle content change
  const handleChange = useCallback((content) => {
    // Sanitize HTML to remove dangerous tags
    const sanitized = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'pre', 'code', 'span'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel', 'class', 'style'],
      ALLOW_DATA_ATTR: false
    });
    
    // Only update if content actually changed (prevents infinite loops)
    if (sanitized === value) return;
    
    // Check character limit
    const textLength = sanitized.replace(/<[^>]*>/g, '').length;
    if (textLength <= maxChars) {
      onChange(sanitized);
      setIsDirty(true);
    }
  }, [onChange, maxChars]);

  // Custom image handler
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        // Check file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
          alert('Image must be less than 2MB');
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', reader.result);
            quill.setSelection(range.index + 1);
          }
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [2, 3, 4, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    history: {
      delay: 1000,
      maxStack: 50,
      userOnly: true
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler]);

  // Quill formats
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'indent',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  // Undo/Redo handlers
  const handleUndo = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) quill.history.undo();
  };

  const handleRedo = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) quill.history.redo();
  };

  // Manual save draft
  const handleSaveDraft = () => {
    if (draftKey && value) {
      localStorage.setItem(`draft_${draftKey}`, value);
      setLastSaved(new Date());
      setIsDirty(false);
    }
  };

  // Clear draft
  const handleClearDraft = () => {
    if (draftKey) {
      localStorage.removeItem(`draft_${draftKey}`);
      setLastSaved(null);
    }
  };

  const charPercentage = (charCount / maxChars) * 100;
  const isNearLimit = charPercentage > 80;
  const isOverLimit = charCount >= maxChars;

  return (
    <div className="rich-text-editor-container" data-testid="rich-text-editor">
      {/* Custom Toolbar Actions */}
      <div className="flex items-center justify-between bg-zinc-100 px-3 py-2 rounded-t-xl border border-b-0 border-zinc-200">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleUndo}
            className="p-1.5 hover:bg-zinc-200 rounded transition-colors"
            title="Undo (Ctrl+Z)"
          >
            <RotateCcw className="w-4 h-4 text-zinc-600" />
          </button>
          <button
            type="button"
            onClick={handleRedo}
            className="p-1.5 hover:bg-zinc-200 rounded transition-colors"
            title="Redo (Ctrl+Y)"
          >
            <RotateCw className="w-4 h-4 text-zinc-600" />
          </button>
          <div className="w-px h-5 bg-zinc-300 mx-1" />
          <button
            type="button"
            onClick={() => setIsPreview(!isPreview)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-sm font-medium transition-colors ${
              isPreview ? 'bg-brand-blue text-white' : 'hover:bg-zinc-200 text-zinc-600'
            }`}
            title="Toggle Preview"
          >
            {isPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {isPreview ? 'Edit' : 'Preview'}
          </button>
        </div>
        <div className="flex items-center gap-3">
          {draftKey && (
            <button
              type="button"
              onClick={handleSaveDraft}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 hover:bg-zinc-200 rounded transition-colors"
              title="Save Draft"
            >
              <Save className="w-3 h-3" />
              Save Draft
            </button>
          )}
          {lastSaved && (
            <span className="text-xs text-zinc-400">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Editor or Preview */}
      {isPreview ? (
        <div 
          className="preview-container bg-white border border-zinc-200 rounded-b-xl p-4 min-h-[250px] prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || '<p class="text-zinc-400 italic">No content to preview</p>') }}
        />
      ) : (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder || 'Start typing your content...'}
        />
      )}

      {/* Character Counter & Status Bar */}
      <div className="flex items-center justify-between bg-zinc-50 px-3 py-2 rounded-b-xl border border-t-0 border-zinc-200 -mt-px">
        <div className="flex items-center gap-2">
          <div className="w-24 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${
                isOverLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(charPercentage, 100)}%` }}
            />
          </div>
          <span className={`text-xs font-medium ${
            isOverLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-zinc-500'
          }`}>
            {charCount.toLocaleString()} / {maxChars.toLocaleString()} characters
          </span>
        </div>
        {isDirty && (
          <span className="text-xs text-amber-600 font-medium">● Unsaved changes</span>
        )}
      </div>

      {/* Styles */}
      <style>{`
        .rich-text-editor-container .ql-toolbar {
          border: 1px solid #e4e4e7;
          border-top: none;
          border-radius: 0;
          background: #fafafa;
          padding: 8px;
        }
        .rich-text-editor-container .ql-toolbar .ql-formats {
          margin-right: 8px;
        }
        .rich-text-editor-container .ql-container {
          border: 1px solid #e4e4e7;
          border-top: none;
          border-radius: 0;
          font-size: 15px;
          font-family: inherit;
        }
        .rich-text-editor-container .ql-editor {
          min-height: 250px;
          max-height: 500px;
          overflow-y: auto;
          line-height: 1.6;
        }
        .rich-text-editor-container .ql-editor.ql-blank::before {
          color: #a1a1aa;
          font-style: normal;
        }
        .rich-text-editor-container .ql-editor h2 {
          font-size: 1.5em;
          font-weight: 700;
          margin: 1em 0 0.5em;
          color: #18181b;
        }
        .rich-text-editor-container .ql-editor h3 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 1em 0 0.5em;
          color: #27272a;
        }
        .rich-text-editor-container .ql-editor h4 {
          font-size: 1.1em;
          font-weight: 600;
          margin: 1em 0 0.5em;
          color: #3f3f46;
        }
        .rich-text-editor-container .ql-editor p {
          margin-bottom: 0.75em;
        }
        .rich-text-editor-container .ql-editor ul,
        .rich-text-editor-container .ql-editor ol {
          padding-left: 1.5em;
          margin-bottom: 1em;
        }
        .rich-text-editor-container .ql-editor li {
          margin-bottom: 0.25em;
        }
        .rich-text-editor-container .ql-editor blockquote {
          border-left: 4px solid #0891b2;
          padding-left: 1em;
          margin: 1em 0;
          color: #52525b;
          font-style: italic;
        }
        .rich-text-editor-container .ql-editor pre {
          background: #f4f4f5;
          padding: 1em;
          border-radius: 8px;
          overflow-x: auto;
          font-family: monospace;
          font-size: 0.9em;
        }
        .rich-text-editor-container .ql-editor a {
          color: #0891b2;
          text-decoration: underline;
        }
        .rich-text-editor-container .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1em 0;
        }
        .rich-text-editor-container .ql-snow .ql-picker {
          font-size: 13px;
        }
        .rich-text-editor-container .ql-snow .ql-tooltip {
          z-index: 100;
        }
        .rich-text-editor-container .preview-container {
          line-height: 1.6;
        }
        .rich-text-editor-container .preview-container h2 {
          font-size: 1.5em;
          font-weight: 700;
          margin: 1em 0 0.5em;
        }
        .rich-text-editor-container .preview-container h3 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 1em 0 0.5em;
        }
        .rich-text-editor-container .preview-container h4 {
          font-size: 1.1em;
          font-weight: 600;
          margin: 1em 0 0.5em;
        }
        .rich-text-editor-container .preview-container ul,
        .rich-text-editor-container .preview-container ol {
          padding-left: 1.5em;
          margin-bottom: 1em;
        }
        .rich-text-editor-container .preview-container img {
          max-width: 100%;
          border-radius: 8px;
        }
        .rich-text-editor-container .preview-container a {
          color: #0891b2;
          text-decoration: underline;
        }
        .rich-text-editor-container .preview-container blockquote {
          border-left: 4px solid #0891b2;
          padding-left: 1em;
          margin: 1em 0;
          color: #52525b;
          font-style: italic;
        }
        
        /* Responsive */
        @media (max-width: 640px) {
          .rich-text-editor-container .ql-toolbar {
            padding: 4px;
          }
          .rich-text-editor-container .ql-toolbar .ql-formats {
            margin-right: 4px;
          }
          .rich-text-editor-container .ql-editor {
            min-height: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
